/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'isomorphic-dompurify';
import { StyledTab, StyledTabPage } from './TabContainer.styles';
import {
  Button,
  Icon,
  Heading,
  headingType,
  size,
  RichText,
  getDirection,
  useCheckBreakpoint,
} from '@marriott/mi-ui-library';
import { TabProps } from './TabContainer.types';
import { clsx } from 'clsx';
import Glide from '@glidejs/glide';
import { useDebouncedCallback } from 'use-debounce';

export const TabContainer = (props: TabProps) => {
  const tabProps = props;
  const {
    componentId,
    trackingProperties,
    tabDetails,
    headerTag,
    header,
    headerFontSize,
    subHeader,
    subHeaderTag,
    subFontSize,
    descriptionText,
    descFontSize,
    contentBlockAlignment,
    layoutContainer,
    children,
    isEditMode = false,
    styleclass,
    customClass,
    flexibleTabWidth,
    tabsPerViewInDesktop,
  } = tabProps;

  const [cardsPerView, setCardsPerView] = useState(2);
  const [_, setSanitizedTabs] = useState<{ tabName: string; tabHtml: string; tabLabel: string; tabIcon?: string }[]>(
    []
  );

  const childrenArray = React.Children.toArray(children).filter(Boolean);
  const [activeStates, setActiveStates] = useState(new Array(childrenArray?.length).fill(false));
  const [activeTab, setActiveTab] = useState(0);
  const isAlternate = styleclass?.includes('alternate');
  const isInverse = styleclass?.includes('inverse');
  const arrowStyle = isAlternate ? 'alt-arrow' : isInverse ? 'inv-arrow' : '';
  const descriptionTextRef = useRef<HTMLDivElement>(null);
  const tabNameTextRef = useRef<HTMLDivElement>(null);
  const isSlideArrowsVisible = tabDetails?.length > cardsPerView;
  const borderEnabled = layoutContainer === 'with_border';
  const tabRequired = true;
  const tabNotRequired = false;
  const containerRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tabItemsRef = useRef<any>([]);
  /**
   * return's the direction true / fasle based on('rtl' / 'ltr')...
   */
  const dir = getDirection();

  /**
   * establish the overall spacing for each tab button, since we need it to measure content widths accurately
   */
  const isSmallAndAbove = useCheckBreakpoint('viewportS');
  const tabBtnPaddingLeft = 19;
  const tabBtnPaddingRight = isSmallAndAbove ? 24 : 19;

  //returns the true/fasle based on rtl/ltr
  const [direction, setDirection] = useState(false);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (isEditMode) {
      setActiveStates(new Array(childrenArray?.length).fill(true));
    } else {
      setActiveStates(new Array(childrenArray?.length).fill(false).map((_, index) => index === activeTab));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, activeTab, children?.length]);

  const handleTabClick = (tabidx: number) => {
    setActiveTab(tabidx);
    setActiveStates(prevStates => prevStates.map((_, index) => index === tabidx));
  };

  const handleResize = useDebouncedCallback(() => {
    /**
     * Calculating the Width of container excluding arrow buttons
     */
    const divWidth = (containerRef?.current?.offsetWidth && containerRef?.current?.offsetWidth - 160) || 0;

    const tabElements = tabItemsRef?.current;
    /**
     * Calculating the Width of the all tab Elements
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tabElementswidth = tabElements?.map((el: any): number => {
      return (
        (el?.querySelector('[data-testid$=TabContainerTabName]')?.getBoundingClientRect()?.width ?? 0) +
        tabBtnPaddingLeft +
        tabBtnPaddingRight
      );
    });

    /**
     * Finds the longest Tab width
     */
    const longestTabWidth = tabElementswidth?.reduce((acc: number, width: number) => (width > acc ? width : acc), 0);

    /**
     * Finds the tabs to be in the View
     */
    const tabsInAView = Math.floor(divWidth / longestTabWidth) || 1;

    const isDesktopView = window?.matchMedia('(min-width: 992px)');
    /**
     * set's true if direction is rtl else false...
     */
    setDirection(dir);

    isDesktopView?.matches && flexibleTabWidth && tabsPerViewInDesktop === tabDetails?.length
      ? setCardsPerView(tabsPerViewInDesktop)
      : setCardsPerView(tabsInAView);
  }, 50);

  useEffect(() => {
    const carouselRootSelector = `#${componentId}`;
    let glide: Glide.Properties | Glide.Static;
    const leftArrow = document?.querySelector(`#${componentId}_left_arrow`);
    const rightArrow = document?.querySelector(`#${componentId}_right_arrow`);
    const preventDefault = (e: any) => e.preventDefault();
    if (document?.querySelector(carouselRootSelector)) {
      try {
        glide = new Glide(carouselRootSelector, {
          type: 'slider',
          perView: cardsPerView,
          startAt: 0,
          rewind: false,
          direction: direction ? 'rtl' : 'ltr',
          bound: true,
          gap: 1,
        }).mount({ ArrowDisabler });

        leftArrow?.addEventListener('touchstart', preventDefault);
        rightArrow?.addEventListener('touchstart', preventDefault);
      } catch (error) {
        // log.error(`Error in initializing Glide for ${componentId}`, error);
      }
    }
    return () => {
      if (glide) {
        (glide as Glide.Properties)?.destroy();
      }
      leftArrow?.removeEventListener('touchstart', preventDefault);
      rightArrow?.removeEventListener('touchstart', preventDefault);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentId, cardsPerView, direction]);

  const ArrowDisabler = function (Glide: any, Components: any) {
    return {
      mount() {
        // Only in effect when rewinding is disabled
        if (Glide.settings.rewind) {
          return;
        }
        const left = document?.querySelector('.glide__arrow--left');

        left?.classList.remove('is-disabled');
        Glide.on(['mount.after', 'run'], () => {
          // Filter out arrows_control
          for (const controlItem of Components.Controls.items) {
            if (controlItem.className !== 'glide__arrows') {
              continue;
            }

            // Set left arrow state
            const leftArrow = controlItem?.querySelector(direction ? '.glide__arrow--right' : '.glide__arrow--left');
            const rightArrow = controlItem?.querySelector(direction ? '.glide__arrow--left' : '.glide__arrow--right');

            if (leftArrow) {
              if (Glide.index === 0) {
                leftArrow?.classList?.add('is-disabled'); // Disable on first slide
                leftArrow?.classList?.remove('glide__arrow--disabled'); // Ensure old class is removed
              } else {
                leftArrow?.classList?.remove('is-disabled'); // Enable on other slides
                leftArrow?.classList?.remove('glide__arrow--disabled'); // Ensure old class is removed
              }
            }

            // Set right arrow state
            if (rightArrow) {
              if (Glide.index === Components.Sizes.length - Glide.settings.perView) {
                rightArrow?.classList?.add('is-disabled'); // Disable on last slide
                rightArrow?.classList?.remove('glide__arrow--disabled'); // Ensure old class is removed
              } else {
                rightArrow?.classList?.remove('is-disabled'); // Disable on other slides
                rightArrow?.classList?.remove('glide__arrow--disabled'); // Ensure old class is removed
              }
            }
          }
        });
      },
    };
  };

  const headerText = () =>
    header && (
      <Heading
        customClass={clsx('mb-0')}
        variation={headingType.title}
        fontSize={
          headerFontSize?.[headerFontSize?.length - 1] as
            | size.small
            | size.medium
            | size.large
            | size.extraSmall
            | undefined
        }
        element={headerTag}
        dangerouslySet={header}
      />
    );

  const subHeaderText = () =>
    subHeader && (
      <Heading
        element={subHeaderTag}
        variation={headingType.subtitle}
        customClass={clsx(!header ? 'mt-3' : '', 'mb-0')}
        dangerouslySet={subHeader}
        fontSize={
          subFontSize?.[subFontSize?.length - 1] as size.small | size.medium | size.large | size.extraSmall | undefined
        }
      />
    );

  const descriptionBlock = () =>
    descriptionText && (
      <RichText
        text={descriptionText}
        ref={descriptionTextRef}
        componentId={componentId + '-TabContainerDescriptionText'}
        customClass={clsx(!subHeader && !header ? 'mt-3' : '', descFontSize)}
      />
    );

  const TabContainerHeader = () => {
    return (
      <div
        className={clsx(
          'tab-container-header-section d-flex flex-column',
          contentBlockAlignment === 'center' ? 'align-items-center text-center' : '',
          contentBlockAlignment === 'left' ? 'align-items-start' : ''
        )}
      >
        {headerText()}
        {subHeaderText()}
        {descriptionBlock()}
      </div>
    );
  };

  const stripHtml = (html: string, tabRequired: boolean): string => {
    if (typeof html !== 'string') return '';
    const sanitizedFragment = DOMPurify.sanitize(html, { FORBID_TAGS: ['p'], RETURN_DOM: true });
    const sanitizedText = sanitizedFragment.textContent?.trim() || '';
    return tabRequired ? sanitizedText + '-tab' : sanitizedText;
  };
  const sanitizedTabs = useMemo(() => {
    if (!tabDetails?.length) return [];

    return tabDetails.map(tab => {
      const { tabName = '', tabIcon } = tab;
      const tabHtml = stripHtml(tabName, tabRequired);
      const tabLabel = stripHtml(tabName, tabNotRequired);
      return { tabHtml, tabLabel, tabIcon, tabName };
    });
  }, [tabDetails, tabRequired, tabNotRequired]);

  return (
    <StyledTab
      data-component-name="m-ui-library-TabContainer"
      data-testid="ui-library-TabContainer"
      className={clsx(styleclass, customClass)}
      tabBtnPaddingLeft={tabBtnPaddingLeft}
      tabBtnPaddingRight={tabBtnPaddingRight}
    >
      <div className="container px-0" ref={containerRef}>
        {TabContainerHeader()}
        <div id={`${componentId}`} className={clsx(`glide col-12 px-0`)}>
          <div
            className={clsx(
              'glide__track',
              !isSlideArrowsVisible ? 'd-flex flex-column' : '',
              !isSlideArrowsVisible && contentBlockAlignment === 'center' ? 'align-items-center' : '',
              !isSlideArrowsVisible && contentBlockAlignment === 'left' ? 'align-items-start' : ''
            )}
            data-glide-el="track"
          >
            <ul
              role="tablist"
              className={clsx(
                `m-standard-tab-list`,
                'glide__slides',
                !isSlideArrowsVisible ? 'tabs-only' : '',
                !isSlideArrowsVisible && contentBlockAlignment === 'center' ? 'justify-content-center' : '',
                !isSlideArrowsVisible && contentBlockAlignment === 'left' ? 'justify-content-start' : ''
              )}
            >
              {sanitizedTabs?.map((tab, key) => {
                const { tabName, tabIcon, tabHtml, tabLabel } = tab;
                return (
                  <li
                    key={key}
                    className={clsx('custom_click_track', `m-standard-tab-list-item`, { active: activeTab === key })}
                    {...{
                      custom_click_track_value: tabHtml,
                    }}
                    role="tab"
                    aria-label={tabLabel}
                    ref={el => (tabItemsRef.current[key] = el)}
                  >
                    <Button
                      ariaExpanded={tabLabel}
                      custom_click_track_value={
                        trackingProperties?.clickTrack
                          ? `${trackingProperties?.trackingDescription}|${tabHtml}|internal`
                          : `${tabHtml}`
                      }
                      callback={() => handleTabClick(key)}
                      className={'tabIconText'}
                    >
                      <Icon iconClass={`${tabIcon}  iconContainer`} />
                      <RichText
                        text={tabName}
                        ref={tabNameTextRef}
                        componentId={componentId + '-TabContainerTabName'}
                        customClass={clsx('text-center mb-2', props?.componentPath ? descFontSize : 't-font-m')}
                      />
                    </Button>
                  </li>
                );
              })}
            </ul>
          </div>
          {!isEditMode && isSlideArrowsVisible && (
            <div className="center-align">
              <div className="carouselControlType1 tab-controls">
                <div className="glide__arrows" data-glide-el="controls">
                  <button
                    className={clsx(
                      direction ? 'right-arrow glide__arrow--right' : 'left-arrow glide__arrow--left',
                      arrowStyle
                    )}
                    id={direction ? `${componentId}_right_arrow` : `${componentId}_left_arrow`}
                    data-glide-dir={direction ? '>' : '<'}
                    aria-label={direction ? 'right-arrow' : 'left-arrow'}
                    data-custom_click_track_value={`internal`}
                  >
                    <span
                      className={direction ? 'icon-arrow-right' : 'icon-arrow-left'}
                      id={direction ? `${componentId}_right_arrow` : `${componentId}_left_arrow`}
                      data-id={direction ? 'right' : 'left'}
                    ></span>
                  </button>
                </div>
                <div className="glide__arrows" data-glide-el="controls">
                  <button
                    className={clsx(
                      direction ? 'left-arrow glide__arrow--left' : 'right-arrow glide__arrow--right',
                      arrowStyle
                    )}
                    id={clsx(direction ? `${componentId}_left_arrow` : `${componentId}_right_arrow`)}
                    data-glide-dir={direction ? '<' : '>'}
                    aria-label={direction ? 'left-arrow' : 'right-arrow'}
                    data-custom_click_track_value={`internal`}
                  >
                    <span
                      className={direction ? 'icon-arrow-left' : 'icon-arrow-right'}
                      id={direction ? `${componentId}_left_arrow` : `${componentId}_right_arrow`}
                      data-id={direction ? 'left' : 'right'}
                    ></span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        <div
          className={clsx(
            'tabpage-container',
            borderEnabled && 'tabpage-border',
            !isSlideArrowsVisible && 'tabpage-edge'
          )}
        >
          {childrenArray?.map((child, index) => (
            <div key={index} className={activeStates[index] ? 'd-block' : 'd-none'}>
              <StyledTabPage
                itemLength={
                  React.isValidElement(child)
                    ? child?.props?.children?.[0]?.props?.children?.props?.headerListItems?.length
                    : 1
                }
              >
                {React.isValidElement(child)
                  ? React.cloneElement(child, { key: index + '-activeindex-' + activeTab, ...(child.props ?? {}) })
                  : child}
              </StyledTabPage>
            </div>
          ))}
        </div>
      </div>
    </StyledTab>
  );
};
